// ══════════════════════════════════════════════════════════════
// PAYDIRT AUTH MODAL
//
// Centered overlay with blurred backdrop.
// Login / Register tabs with inline validation.
// Calls AuthContext login() / register() on submit.
// ══════════════════════════════════════════════════════════════

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';

export default function AuthModal({ onSuccess }) {
  const { authVisible, hideAuth, login, register, authError } = useAuth();
  const [mode, setMode]           = useState('login');
  const [identifier, setIdentifier] = useState('');
  const [username, setUsername]   = useState('');
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [showPw, setShowPw]       = useState(false);
  const [fieldError, setFieldError] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const firstInput = useRef(null);

  // Focus first input when modal opens
  useEffect(() => {
    if (authVisible) {
      setTimeout(() => firstInput.current?.focus(), 100);
    }
  }, [authVisible, mode]);

  // Reset form when switching modes
  useEffect(() => {
    setIdentifier('');
    setUsername('');
    setEmail('');
    setPassword('');
    setFieldError({});
    setShowPw(false);
  }, [mode]);

  if (!authVisible) return null;

  // ── Client-side validation ──
  function validate() {
    const errs = {};

    if (mode === 'register') {
      const u = username.trim();
      if (!u) errs.username = 'Required';
      else if (u.length < 3) errs.username = 'Min 3 characters';
      else if (u.length > 24) errs.username = 'Max 24 characters';
      else if (!/^[a-zA-Z0-9_-]+$/.test(u)) errs.username = 'Letters, numbers, hyphens, underscores only';

      const e = email.trim();
      if (!e) errs.email = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) errs.email = 'Invalid email';
    } else {
      if (!identifier.trim()) errs.identifier = 'Required';
    }

    if (!password) errs.password = 'Required';
    else if (password.length < 8) errs.password = 'Min 8 characters';
    else if (password.length > 72) errs.password = 'Max 72 characters';

    setFieldError(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);

    let result;
    if (mode === 'login') {
      result = await login(identifier.trim(), password);
    } else {
      result = await register(username.trim(), email.trim().toLowerCase(), password);
    }

    setSubmitting(false);

    if (result.ok) {
      onSuccess?.(result.user, mode === 'register' ? 'register' : 'login');
    } else if (result.field) {
      // Server told us which field has the error
      setFieldError(prev => ({ ...prev, [result.field]: result.error }));
    }
  }

  // ── Styles ──
  const inputWrap = { position: 'relative' };

  const inputStyle = (hasError) => ({
    width: '100%',
    background: 'var(--bs-bg, #0a0a0a)',
    border: `1px solid ${hasError ? '#ff174466' : 'var(--bs-border, #222)'}`,
    borderRadius: 8,
    padding: '12px 14px',
    color: 'var(--bs-ink, #fff)',
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    outline: 'none',
    transition: 'border-color .2s',
  });

  const labelStyle = {
    fontSize: 10,
    fontFamily: "'JetBrains Mono', monospace",
    color: 'var(--bs-muted, #555)',
    letterSpacing: 1.5,
    display: 'block',
    marginBottom: 5,
  };

  const errStyle = {
    fontSize: 10,
    fontFamily: "'JetBrains Mono', monospace",
    color: 'var(--bs-red, #ff1744)',
    marginTop: 4,
  };

  return (
    <div
      onClick={hideAuth}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'authFadeIn .2s ease',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bs-surface, #111)',
          border: '1px solid var(--bs-border, #222)',
          borderRadius: 20,
          width: '92%',
          maxWidth: 400,
          padding: '28px 24px',
          position: 'relative',
          boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px #00e67611',
          animation: 'authScaleIn .25s cubic-bezier(.2,.8,.3,1)',
        }}
      >
        {/* Close button */}
        <button
          onClick={hideAuth}
          style={{
            position: 'absolute', top: 14, right: 16,
            background: 'none', border: 'none',
            color: 'var(--bs-muted, #444)', fontSize: 20, cursor: 'pointer', lineHeight: 1,
          }}
        >
          ✕
        </button>

        {/* Brand header */}
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <span style={{
            fontSize: 32, fontFamily: "'Dela Gothic One', serif",
            color: 'var(--bs-green, #00e676)', textShadow: '0 0 30px #00e67633',
          }}>
            $
          </span>
          <h3 style={{
            fontSize: 20, fontFamily: "'Dela Gothic One', serif",
            color: 'var(--bs-ink, #fff)', marginTop: 4, letterSpacing: 2,
          }}>
            {mode === 'login' ? 'WELCOME BACK' : 'JOIN PAY DIRT'}
          </h3>
          <p style={{
            fontSize: 11, color: 'var(--bs-muted, #444)',
            fontFamily: "'JetBrains Mono', monospace", marginTop: 4,
          }}>
            {mode === 'login' ? 'Sign in to place bets' : 'Create your account — 1,000 credit welcome bonus'}
          </p>
        </div>

        {/* Tab toggle */}
        <div style={{ display: 'flex', marginBottom: 18, borderBottom: '1px solid var(--bs-border, #1a1a1a)' }}>
          {['login', 'register'].map(t => (
            <button
              key={t}
              onClick={() => setMode(t)}
              style={{
                flex: 1, padding: '10px 0',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
                fontWeight: mode === t ? 700 : 400, letterSpacing: 1,
                color: mode === t ? 'var(--bs-ink, #fff)' : 'var(--bs-muted, #555)',
                borderBottom: mode === t ? '2px solid var(--bs-green, #00e676)' : '2px solid transparent',
                transition: 'color .15s',
              }}
            >
              {t.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Welcome bonus banner on register */}
        {mode === 'register' && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', marginBottom: 16,
            background: 'var(--bs-green-bg, #00e67608)', border: '1px solid var(--bs-green-bd, #00e67622)',
            borderRadius: 10,
          }}>
            <span style={{ fontSize: 20 }}>🎁</span>
            <div>
              <div style={{
                fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 700, color: 'var(--bs-green, #00e676)',
              }}>
                1,000 CREDIT WELCOME BONUS
              </div>
              <div style={{ fontSize: 10, color: 'var(--bs-muted, #666)', marginTop: 1 }}>
                Start betting on credibility immediately
              </div>
            </div>
          </div>
        )}

        {/* Server-level error */}
        {authError && !fieldError[Object.keys(fieldError)[0]] && (
          <div style={{
            padding: '10px 14px', marginBottom: 14,
            background: 'var(--bs-red-bg, #ff174411)', border: '1px solid var(--bs-red-bd, #ff174433)',
            borderRadius: 8, fontSize: 11, color: 'var(--bs-red, #ff1744)',
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {authError}
          </div>
        )}

        {/* Form fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {mode === 'register' && (
            <div>
              <label style={labelStyle}>USERNAME</label>
              <input
                ref={firstInput}
                style={inputStyle(fieldError.username)}
                placeholder="your_handle"
                value={username}
                onChange={e => { setUsername(e.target.value); setFieldError(p => ({ ...p, username: null })); }}
                maxLength={24}
                autoComplete="username"
              />
              {fieldError.username && <div style={errStyle}>{fieldError.username}</div>}
            </div>
          )}

          {mode === 'register' ? (
            <div>
              <label style={labelStyle}>EMAIL</label>
              <input
                style={inputStyle(fieldError.email)}
                placeholder="you@example.com"
                value={email}
                onChange={e => { setEmail(e.target.value); setFieldError(p => ({ ...p, email: null })); }}
                type="email"
                autoComplete="email"
              />
              {fieldError.email && <div style={errStyle}>{fieldError.email}</div>}
            </div>
          ) : (
            <div>
              <label style={labelStyle}>EMAIL OR USERNAME</label>
              <input
                ref={firstInput}
                style={inputStyle(fieldError.identifier)}
                placeholder="email or username"
                value={identifier}
                onChange={e => { setIdentifier(e.target.value); setFieldError(p => ({ ...p, identifier: null })); }}
                autoComplete="username"
              />
              {fieldError.identifier && <div style={errStyle}>{fieldError.identifier}</div>}
            </div>
          )}

          <div>
            <label style={labelStyle}>PASSWORD</label>
            <div style={inputWrap}>
              <input
                type={showPw ? 'text' : 'password'}
                style={{ ...inputStyle(fieldError.password), paddingRight: 50 }}
                placeholder={mode === 'register' ? 'min 8 characters' : '••••••••'}
                value={password}
                onChange={e => { setPassword(e.target.value); setFieldError(p => ({ ...p, password: null })); }}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                style={{
                  position: 'absolute', right: 12, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none',
                  color: 'var(--bs-muted, #555)', cursor: 'pointer',
                  fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {showPw ? 'HIDE' : 'SHOW'}
              </button>
            </div>
            {fieldError.password && <div style={errStyle}>{fieldError.password}</div>}
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width: '100%', marginTop: 20, padding: '14px 0',
            borderRadius: 10, border: 'none',
            background: submitting
              ? '#333'
              : 'linear-gradient(135deg, #00e676, #00c853)',
            color: submitting ? '#666' : '#000',
            fontSize: 14, fontFamily: "'Dela Gothic One', serif",
            letterSpacing: 2, cursor: submitting ? 'default' : 'pointer',
            transition: 'opacity .2s',
          }}
        >
          {submitting
            ? 'PLEASE WAIT...'
            : mode === 'login' ? 'SIGN IN' : 'CREATE ACCOUNT'}
        </button>

        {/* Footer link */}
        {mode === 'login' && (
          <p style={{
            textAlign: 'center', marginTop: 14,
            fontSize: 10, color: 'var(--bs-muted, #444)',
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            Forgot password?{' '}
            <span style={{ color: 'var(--bs-green, #00e676)', cursor: 'pointer' }}>Reset</span>
          </p>
        )}
      </div>

      {/* Animations */}
      <style>{`
        @keyframes authFadeIn {
          from { opacity: 0 }
          to { opacity: 1 }
        }
        @keyframes authScaleIn {
          from { opacity: 0; transform: scale(.93) }
          to { opacity: 1; transform: scale(1) }
        }
      `}</style>
    </div>
  );
}
